import React, { useCallback, useEffect, useState } from "react";

import { FunnelChart } from "../components/charts/FunnelChart";
import { LinkedInPanel } from "../components/LinkedInPanel";
import { api } from "../lib/api";
import { formatCurrency, formatPercent } from "../lib/format";
import {
  AnalyticsFunnelRow,
  ChannelRoiResponse,
  ChannelRoiRow,
  CycleResponse,
  Pipeline,
  RevenueResponse,
  RevenueRow,
} from "../types/crm";

type Tab = "canais" | "funil" | "receita";
const YEARS = [2026, 2025];

export function AnalyticsPage() {
  const [tab, setTab] = useState<Tab>("canais");
  const [year, setYear] = useState<number>(2026);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);

  useEffect(() => {
    api.get<Pipeline[]>("/pipelines/").then((r) => setPipelines(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Analíticos</h1>
          <p className="mt-0.5 text-sm text-slate-500">Desempenho comercial da MINEHR</p>
        </div>
        <select
          aria-label="Ano"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="h-9 rounded-[10px] border border-border-subtle bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-b border-border-subtle">
        {([
          ["canais", "Canais"],
          ["funil", "Funil de Vendas"],
          ["receita", "Desdobramento de Receita"],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "canais" && <ChannelsTab year={year} />}
      {tab === "funil" && <FunnelTab pipelines={pipelines} />}
      {tab === "receita" && <RevenueTab year={year} />}
    </div>
  );
}

function ChannelsTab({ year }: { year: number }) {
  const [rows, setRows] = useState<ChannelRoiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLinkedIn, setShowLinkedIn] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<ChannelRoiResponse>(`/analytics/channels-roi/?year=${year}`)
      .then((r) => setRows(r.data.rows))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => load(), [load]);

  const saveInvest = async (channelId: number, value: string) => {
    await api.post("/analytics/channels-roi/", { year, channel: channelId, investimento: value || null });
    load();
  };

  if (loading) return <p className="mt-6 text-sm text-slate-500">Carregando...</p>;

  return (
    <div className="mt-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <div key={row.channel_id} className="rounded-[10px] border border-border-subtle bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">{row.channel_name}</p>
              <span className="rounded-full bg-primary-soft px-2 py-0.5 text-xs font-medium text-primary">
                {row.leads} leads
              </span>
            </div>
            <div className="mt-3 rounded-[10px] bg-surface p-3">
              <label className="block text-xs font-medium text-slate-500">Investimento ({year})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                defaultValue={row.investimento ?? ""}
                placeholder="—"
                onBlur={(e) => saveInvest(row.channel_id, e.target.value)}
                className="mt-1 w-full rounded-[8px] border border-border-subtle bg-white px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-slate-500">ROI</span>
                <span className={`font-semibold ${row.roi === null ? "text-slate-300" : row.roi >= 0 ? "text-accent-dark" : "text-red-600"}`}>
                  {row.roi === null ? "—" : formatPercent(row.roi)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-slate-500">Ganhos</span>
                <span className="text-slate-700">{row.ganhos}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setShowLinkedIn((v) => !v)}
        className="mt-4 text-sm font-medium text-primary hover:underline"
      >
        {showLinkedIn ? "Ocultar detalhamento (LinkedIn) ▲" : "Ver detalhamento por publicação (LinkedIn) ▼"}
      </button>
      {showLinkedIn && <LinkedInPanel />}
    </div>
  );
}

function FunnelTab({ pipelines }: { pipelines: Pipeline[] }) {
  const [visao, setVisao] = useState<string>("");
  const [rows, setRows] = useState<AnalyticsFunnelRow[]>([]);
  const [cycle, setCycle] = useState<CycleResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // default: funil padrão
  useEffect(() => {
    if (!visao && pipelines.length) {
      const def = pipelines.find((p) => p.is_default) ?? pipelines[0];
      setVisao(def.slug);
    }
  }, [pipelines, visao]);

  useEffect(() => {
    if (!visao) return;
    setLoading(true);
    Promise.all([
      api.get<AnalyticsFunnelRow[]>(`/analytics/funnel/?pipeline=${visao}`),
      api.get<CycleResponse>(`/analytics/cycle/?pipeline=${visao}`),
    ])
      .then(([f, c]) => {
        setRows(f.data);
        setCycle(c.data);
      })
      .finally(() => setLoading(false));
  }, [visao]);

  const visaoNome =
    visao === "consolidado"
      ? "Consolidado"
      : pipelines.find((p) => p.slug === visao)?.name ?? "";

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-500">Visão:</span>
        {pipelines.map((p) => (
          <button
            key={p.slug}
            type="button"
            onClick={() => setVisao(p.slug)}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              visao === p.slug ? "bg-primary text-white" : "bg-surface text-slate-600 hover:bg-primary-soft"
            }`}
          >
            {p.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setVisao("consolidado")}
          className={`rounded-full px-3 py-1 text-sm font-medium ${
            visao === "consolidado" ? "bg-primary text-white" : "bg-surface text-slate-600 hover:bg-primary-soft"
          }`}
        >
          Consolidado
        </button>
      </div>

      <div className="mt-4 rounded-[10px] border border-border-subtle bg-white p-5">
        <p className="text-sm font-semibold text-slate-800">
          {visao === "consolidado" ? "Funil consolidado" : `Funil · ${visaoNome}`}
        </p>
        <p className="text-xs text-slate-500">
          {visao === "consolidado" ? "Venda nova + Renovações" : `${rows.length} fases`}
        </p>
        <div className="mt-4 h-72">
          {loading ? (
            <p className="text-sm text-slate-500">Carregando...</p>
          ) : rows.every((r) => r.oportunidades_count === 0) ? (
            <p className="py-8 text-center text-sm text-slate-400">Sem dados para este funil ainda.</p>
          ) : (
            <FunnelChart rows={rows} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-[10px] border border-primary bg-primary p-5">
          <p className="text-xs text-primary-soft">Ciclo médio de vendas</p>
          <p className="mt-1 text-2xl font-semibold text-white">
            {cycle?.ciclo_medio_dias != null ? `${cycle.ciclo_medio_dias} dias` : "—"}
          </p>
          <p className="mt-1 text-xs text-primary-soft">
            Da criação até o fechamento (negócios já fechados).
          </p>
        </div>
        <div className="rounded-[10px] border border-border-subtle bg-white p-5">
          <p className="text-sm font-semibold text-slate-800">Tempo médio por etapa</p>
          <div className="mt-3 space-y-2">
            {(cycle?.por_etapa ?? []).length === 0 && (
              <p className="text-sm text-slate-400">Sem histórico suficiente ainda.</p>
            )}
            {(cycle?.por_etapa ?? []).map((e) => {
              const max = Math.max(
                1,
                ...(cycle?.por_etapa ?? []).map((x) => x.dias_medios ?? 0)
              );
              const w = e.dias_medios ? Math.max(4, (e.dias_medios / max) * 100) : 0;
              return (
                <div key={e.stage_name}>
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>{e.stage_name}</span>
                    <span>{e.dias_medios != null ? `${e.dias_medios} d` : "—"}</span>
                  </div>
                  <div className="mt-0.5 h-2 rounded-full bg-surface">
                    <div className="h-2 rounded-full bg-accent" style={{ width: `${w}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function RevenueTab({ year }: { year: number }) {
  const [rows, setRows] = useState<RevenueRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<RevenueResponse>(`/analytics/revenue/?year=${year}`)
      .then((r) => setRows(r.data.rows))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => load(), [load]);

  const save = async (productId: number, field: "meta" | "alcancado", value: string) => {
    await api.post("/analytics/revenue/", { year, product: productId, [field]: value || null });
    load();
  };

  if (loading) return <p className="mt-6 text-sm text-slate-500">Carregando...</p>;

  const totalEmNeg = rows.reduce((s, r) => s + Number(r.em_negociacao), 0);
  const totalMeta = rows.reduce((s, r) => s + Number(r.meta ?? 0), 0);
  const totalAlc = rows.reduce((s, r) => s + Number(r.alcancado ?? 0), 0);
  const pctMeta = totalMeta > 0 ? totalAlc / totalMeta : null;

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <SummaryCard label="Em negociação" value={formatCurrency(totalEmNeg)} />
        <SummaryCard label={`Meta anual ${year}`} value={totalMeta > 0 ? formatCurrency(totalMeta) : "—"} highlight />
        <SummaryCard label="Alcançado" value={totalAlc > 0 ? formatCurrency(totalAlc) : "—"} />
        <SummaryCard label="% da meta" value={pctMeta === null ? "—" : formatPercent(pctMeta)} />
      </div>

      <div className="overflow-x-auto rounded-[10px] border border-border-subtle">
        <table className="min-w-full divide-y divide-border-subtle text-sm">
          <thead className="bg-surface">
            <tr>
              {["Produto", "Pipeline", "Em negociação", "Meta projetada", "Alcançado", "% meta"].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((row) => {
              const pct = row.meta && Number(row.meta) > 0 ? Number(row.alcancado ?? 0) / Number(row.meta) : null;
              const pctColor =
                pct === null ? "#374151" : pct >= 1 ? "#166534" : pct >= 0.6 ? "#92400E" : "#374151";
              return (
                <tr key={row.product_id}>
                  <td className="px-4 py-2 font-medium text-slate-800">{row.product_name}</td>
                  <td className="px-4 py-2 text-slate-500">{row.pipeline_count} op.</td>
                  <td className="px-4 py-2 text-primary">{formatCurrency(row.em_negociacao)}</td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={row.meta ?? ""}
                      placeholder="—"
                      onBlur={(e) => save(row.product_id, "meta", e.target.value)}
                      className="w-28 rounded-[8px] border border-border-subtle px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={row.alcancado ?? ""}
                      placeholder="—"
                      onBlur={(e) => save(row.product_id, "alcancado", e.target.value)}
                      className="w-28 rounded-[8px] border border-border-subtle px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 py-2 font-semibold" style={{ color: pctColor }}>
                    {pct === null ? "—" : formatPercent(pct)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-[10px] border p-4 ${highlight ? "border-primary bg-primary" : "border-border-subtle bg-white"}`}>
      <p className={`text-xs ${highlight ? "text-primary-soft" : "text-slate-500"}`}>{label}</p>
      <p className={`mt-1 text-lg font-semibold ${highlight ? "text-white" : "text-slate-800"}`}>{value}</p>
    </div>
  );
}
